<script>
	import { browser } from "$app/environment";
	import { getFunctions, httpsCallable } from "firebase/functions";
	import { tick } from "svelte";

	export let col;
	export let settingId;
	export let formName;
	export let responses = {};
	export let messages = [];
	export let conversationId;
	export let chatResponseFormat;
	export let model;
	export let disabled = false;
	export let readonly = false;
	export let onFieldValues = async () => {};
	export let onMessages = async () => {};
	export let onChatSession = async () => {};

	let open = false;
	let input = "";
	let loading = false;
	let starting = false;
	let messagesContainer;
	let messageCounter = 0;

	$: canSend =
		browser &&
		!disabled &&
		!loading &&
		!starting &&
		!!input.trim() &&
		!readonly;

	const startChat = async () => {
		if (conversationId || readonly) return;

		starting = true;

		const functions = getFunctions();
		const chatCallable = httpsCallable(functions, "chat");
		const response = await chatCallable({
			formName,
			col,
			settingId,
		});
		console.log({ response });
		const data = response?.data || {};
		conversationId = data.conversationId;
		chatResponseFormat = data.chatResponseFormat;
		model = data.model;

		await onChatSession({ conversationId, chatResponseFormat, model });

		if (!messages.length) {
			await addMessage("assistant", data.message);
		}

		starting = false;
	};

	const sendMessage = async () => {
		const text = input.trim();
		if (!text || loading || disabled || readonly) return;

		input = "";
		await addMessage("user", text);
		loading = true;

		const functions = getFunctions();
		const chatCallable = httpsCallable(functions, "chat");
		const response = await chatCallable({
			conversationId,
			message: text,
			chatResponseFormat,
			model,
			formName,
			col,
			settingId,
			currentResponses: getCurrentResponses(),
		});
		console.log({ response });
		const data = response?.data || {};
		const assistantPayload = parseAssistantPayload(data.message);

		await onFieldValues({
			fieldValues: assistantPayload.fieldValues || {},
			changedFields: assistantPayload.changedFields || [],
			isComplete: assistantPayload.isComplete === true,
		});

		await addMessage(
			"assistant",
			assistantPayload.reply ||
				"I updated the form with the information you provided.",
		);
		loading = false;
	};

	const parseAssistantPayload = (message) => {
		if (typeof message === "object" && message !== null) {
			return message;
		}

		try {
			return JSON.parse(message);
		} catch {
			return { reply: message };
		}
	};

	const getCurrentResponses = () =>
		Object.entries(responses || {})
			.filter(([key, value]) => {
				if (!key || key === "submitted" || key === "submittedAt")
					return false;
				if (key === "messages") return false;
				if (key === "conversationId") return false;
				if (key === "chatResponseFormat") return false;
				if (key === "model") return false;
				if (value === undefined || value === null || value === "")
					return false;
				return (
					typeof value === "string" ||
					typeof value === "number" ||
					typeof value === "boolean" ||
					Array.isArray(value)
				);
			})
			.reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

	const addMessage = async (role, content) => {
		console.log({ role, content });
		// return;
		const nextMessages = [
			...messages,
			{
				id: `${Date.now()}-${messageCounter++}`,
				role,
				content,
			},
		];
		messages = nextMessages;

		await onMessages(nextMessages);

		await tick();

		if (!messagesContainer) return;
		messagesContainer.scrollTop = messagesContainer.scrollHeight;
	};

	const handleKeydown = (event) => {
		if (event.key !== "Enter" || event.shiftKey) return;

		event.preventDefault();
		sendMessage();
	};

	const toggleOpen = async () => {
		open = !open;
		if (open && !conversationId && !disabled && !readonly) {
			await startChat();
		}
	};
</script>

<section class="mb-4 rounded border border-base-300 bg-base-100 p-3">
	<div class="flex items-center justify-between gap-3">
		<div>
			<h3 class="text-lg font-semibold">Form Assistant</h3>
			<p class="text-sm opacity-75">
				Answer in plain English and the assistant will fill matching
				fields.
			</p>
		</div>
		<button
			type="button"
			class="btn btn-sm btn-outline"
			{disabled}
			on:click={toggleOpen}
			aria-expanded={open}
		>
			{open ? "Hide" : conversationId || readonly ? "Show" : "Start"}
		</button>
	</div>

	{#if open}
		<div
			bind:this={messagesContainer}
			class="mt-3 max-h-80 space-y-2 overflow-y-auto rounded border border-base-300 bg-base-200 p-3"
			role="log"
			aria-live="polite"
		>
			{#if starting}
				<div class="text-sm opacity-75">Starting assistant...</div>
			{/if}

			{#each messages as message (message.id)}
				<div
					class={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
				>
					<div
						class={`max-w-[85%] rounded px-3 py-2 text-sm sm:text-base ${
							message.role === "user"
								? "bg-primary text-primary-content"
								: "bg-base-100"
						}`}
					>
						{message.content}
					</div>
				</div>
			{/each}
		</div>

		<form
			class="mt-3 flex flex-col gap-2 sm:flex-row"
			on:submit|preventDefault={sendMessage}
		>
			<textarea
				class="textarea textarea-bordered min-h-20 flex-1 text-base"
				bind:value={input}
				placeholder="Tell me what to add to the form..."
				disabled={disabled || loading || starting || readonly}
				on:keydown={handleKeydown}
			></textarea>
			<button
				type="submit"
				class={`btn btn-primary sm:self-end ${loading ? "loading" : ""}`}
				disabled={!canSend}
			>
				Send
			</button>
		</form>
	{/if}
</section>
